import { ValidatorSchema } from "../constants/validators";
export type BankParts = {
  key: string;
  branchCode?: string;
  accountNumber?: string;
};

/**
 * Parse an IFSC code into constituent parts.
 * Returns null if the input is invalid.
 * @example
 * parseIfsc("ABCD01234567") => { key: "ABCD", branchCode: "01234567" }
 * parseIfsc("ABCD01234567") => null
 */
export function parseIfsc(input: string | undefined | null): BankParts | null {
  if (!input) return null;
  const code = String(input).trim().toUpperCase();
  const result = ValidatorSchema.payment.bank.ifsc.safeParse(code);
  if (!result.success) return null;
  return {
    key: code.slice(0, 4),
    branchCode: code.slice(5), // last 6 chars
  };
}

/**
 * Parse a CLABE into constituent parts (18 digits: BBB SSS AAAAAAAAAAA C).
 * Returns null if the input is invalid.
 * @example
 * parseClabe("12345678901234567") => { key: "123", branchCode: "456", accountNumber: "7890123456" }
 * parseClabe("AAA") => null
 */
export function parseClabe(input: string | undefined | null): BankParts | null {
  if (!input) return null;
  const code = String(input).trim();
  const result = ValidatorSchema.clabeNumber.safeParse(code);
  if (!result.success) return null;
  return {
    key: code.slice(0, 3), // bank code (BBB)
    branchCode: code.slice(3, 6), // branch code (SSS)
    accountNumber: code.slice(6, 17), // 11-digit account number (positions 6..16)
  };
}

/**
 * Parse a UPI ID into constituent parts (username@bankname).
 * Returns null if the input is invalid.
 * @example
 * parseUpiId("test@test.com") => { key: "test", accountNumber: "test@test.com" }
 * parseUpiId("test@test.com") => null
 */
export function parseUpiId(input: string | undefined | null): BankParts | null {
  if (!input) return null;
  const code = String(input).trim();
  const result = ValidatorSchema.upiId.safeParse(code);
  if (!result.success) return null;
  return {
    key: code.split("@")[1],
    accountNumber: code,
  };
}
